<script setup lang="ts">
import { computed } from 'vue';
import { DateTime } from 'luxon';
import {
  AppointmentStatus,
  AssetContainerState,
  AssetContainerType,
  AssetVisitType
} from '@nova/core';

import type { YardAssetVisitDetails } from '@satellite/composables/useYardAssetVisitPresenterV2';
import type { YardAssetDetails } from '@satellite/composables/useYardAssetPresenterV2';

const CONTAINER_WIDTH = 30;
const CONTAINER_HEIGHT = 76;

const CONTAINER_X = 0;
const CONTAINER_Y = 0;

const HOOK_PATH =
  'M16.2383 58C14.8576 58 13.7383 59.1193 13.7383 60.5C13.7383 61.6028 14.4533 62.5361 15.4443 62.8682V66.1348C15.4443 66.9103 14.8282 67.539 14.0684 67.5391C13.3085 67.5391 12.6924 66.9103 12.6924 66.1348V65.25H13.6309C13.8213 65.25 13.9219 65.0445 13.7949 64.915L11.6445 62.7266C11.5097 62.5895 11.261 62.6762 11.2607 62.8604V65.25H11.2617V66.1348C11.2617 67.7169 12.5182 69 14.0684 69C15.6184 68.9999 16.875 67.7169 16.875 66.1348V62.915C17.9469 62.6332 18.7383 61.6604 18.7383 60.5C18.7383 59.1193 17.619 58 16.2383 58ZM16.2383 59.3789C16.8572 59.3789 17.3594 59.8811 17.3594 60.5C17.3594 61.1189 16.8572 61.6211 16.2383 61.6211C15.6193 61.6211 15.1172 61.1189 15.1172 60.5C15.1172 59.8811 15.6193 59.3789 16.2383 59.3789Z';

const props = withDefaults(
  defineProps<{
    assetVisit: YardAssetVisitDetails;
    orientation?: 'horizontal' | 'vertical';
    loading?: boolean;
    warehouseTimezone?: string;
    showContainerCount?: boolean;
  }>(),
  {
    orientation: 'horizontal',
    loading: false,
    warehouseTimezone: null,
    showContainerCount: false
  }
);

defineOptions({
  inheritAttrs: false
});

const getTheme = (assetVisit: YardAssetVisitDetails | null | undefined) => {
  const appointment = assetVisit?.appointment;
  const warehouseNow = DateTime.now().setZone(props.warehouseTimezone);
  const appointmentEnd = appointment?.end
    ? typeof appointment.end === 'string'
      ? DateTime.fromISO(appointment.end)
      : DateTime.fromJSDate(appointment.end)
    : null;

  if (appointment?.id) {
    if (
      [AppointmentStatus.Cancelled, AppointmentStatus.NoShow].includes(appointment.status) ||
      (appointmentEnd !== null && warehouseNow > appointmentEnd)
    ) {
      return '-past-appointment';
    }

    return '-active-appointment';
  }

  return '-no-appointment';
};

const getAssetContainersFromAssetVisit = (
  assetVisit: YardAssetVisitDetails | null
): YardAssetDetails[] | null => {
  if (!assetVisit || !Array.isArray(assetVisit.assetContainers)) {
    return [];
  }

  return assetVisit.assetContainers;
};

const containerCount = computed(() => getAssetContainersFromAssetVisit(props.assetVisit).length);

const assetContainer = computed(() => {
  if (containerCount.value === 1) {
    return getAssetContainersFromAssetVisit(props.assetVisit)[0];
  }

  return null;
});

const theme = computed(() => getTheme(props.assetVisit));

const resolvedState = computed(() => {
  if (containerCount.value === 1) {
    return assetContainer.value?.state;
  }

  return null;
});

const isUnknownState = computed(() => {
  return ![
    AssetContainerState.Full,
    AssetContainerState.PartiallyLoaded,
    AssetContainerState.Empty
  ].includes(resolvedState.value);
});

const revealWidth = computed(() => {
  const REVEAL_WIDTH_MULTIPLIER_BY_STATE: Record<AssetContainerState, number> = {
    [AssetContainerState.Empty]: 1,
    [AssetContainerState.PartiallyLoaded]: 0.5,
    [AssetContainerState.Full]: 0
  };

  return (REVEAL_WIDTH_MULTIPLIER_BY_STATE[resolvedState.value] ?? 1) * CONTAINER_HEIGHT;
});

const svgDefUid = computed(() => `${props.assetVisit?.id ?? 'new'}`);

const isDetachableVisit = computed(() => {
  return [AssetVisitType.Drop, AssetVisitType.DropHook, AssetVisitType.PickUp].includes(
    props.assetVisit?.visitType
  );
});

const isAssetTrailer = computed(() => {
  if (containerCount.value !== 1) {
    return false;
  }

  return assetContainer.value?.type === AssetContainerType.Trailer;
});

const isAssetContainer = computed(() => {
  if (containerCount.value !== 1) {
    return false;
  }

  return assetContainer.value?.type === AssetContainerType.Container;
});

const isAssetOther = computed(() => {
  if (containerCount.value !== 1) {
    return false;
  }

  return !isAssetContainer.value && !isAssetTrailer.value;
});
</script>

<template>
  <span
    class="asset-visit-state-icon-v2 position-relative"
    v-bind="$attrs"
    :class="{
      [theme]: true,
      'is-full': resolvedState === AssetContainerState.Full,
      'is-partially-loaded': resolvedState === AssetContainerState.PartiallyLoaded,
      'is-empty': resolvedState === AssetContainerState.Empty,
      'is-loading': loading,
      '-horizontal': orientation === 'horizontal',
      '-vertical': orientation === 'vertical'
    }"
    :data-testid="`asset-visit-state-icon-v2${theme}`">
    <template v-if="orientation === 'horizontal'">
      <svg
        class="truck-cab-horizontal"
        viewBox="0 0 100 30"
        fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <path
          d="M2 28C0.895432 28 1.23306e-06 27.1046 1.1365e-06 26L-7.86804e-07 4C-8.83369e-07 2.89543 0.89543 2 2 2L2 28Z"
          fill="var(--stroke-color)" />
        <rect
          x="5.5"
          y="28.5"
          width="5"
          height="9"
          rx="0.5"
          fill="var(--stroke-color)"
          stroke="var(--stroke-color)"
          transform="rotate(-90 5.5 28.5)" />
        <rect
          x="16.5"
          y="28.5"
          width="5"
          height="9"
          rx="0.5"
          fill="var(--stroke-color)"
          stroke="var(--stroke-color)"
          transform="rotate(-90 16.5 28.5)" />
        <rect
          x="52.5"
          y="28.5"
          width="5"
          height="9"
          rx="0.5"
          fill="var(--stroke-color)"
          stroke="var(--stroke-color)"
          transform="rotate(-90 52.5 28.5)" />
        <rect
          x="63.5"
          y="28.5"
          width="5"
          height="9"
          rx="0.5"
          fill="var(--stroke-color)"
          stroke="var(--stroke-color)"
          transform="rotate(-90 63.5 28.5)" />
        <g clip-path="url(#clip0_13257_34912)">
          <mask id="path-6-inside-1_13257_34912" fill="white">
            <path d="M2 23L2 19L76 19L76 23L2 23Z" />
          </mask>
          <path d="M2 23L2 19L76 19L76 23L2 23Z" fill="var(--light-fill-color)" />
          <path
            d="M2 19L2 20L76 20L76 19L76 18L2 18L2 19ZM76 23L76 22L2 22L2 23L2 24L76 24L76 23Z"
            fill="var(--stroke-color)"
            mask="url(#path-6-inside-1_13257_34912)" />
          <mask id="path-8-inside-2_13257_34912" fill="white">
            <path d="M2 11L2 7L76 7L76 11L2 11Z" />
          </mask>
          <path d="M2 11L2 7L76 7L76 11L2 11Z" fill="var(--light-fill-color)" />
          <path
            d="M2 7L2 8L76 8L76 7L76 6L2 6L2 7ZM76 11L76 10L2 10L2 11L2 12L76 12L76 11Z"
            fill="var(--stroke-color)"
            mask="url(#path-8-inside-2_13257_34912)" />
        </g>
        <rect
          x="5.5"
          y="6.5"
          width="5"
          height="9"
          rx="0.5"
          fill="var(--stroke-color)"
          stroke="var(--stroke-color)"
          transform="rotate(-90 5.5 6.5)" />
        <rect
          x="16.5"
          y="6.5"
          width="5"
          height="9"
          rx="0.5"
          fill="var(--stroke-color)"
          stroke="var(--stroke-color)"
          transform="rotate(-90 16.5 6.5)" />
        <rect
          x="52.5"
          y="6.5"
          width="5"
          height="9"
          rx="0.5"
          fill="var(--stroke-color)"
          stroke="var(--stroke-color)"
          transform="rotate(-90 52.5 6.5)" />
        <rect
          x="63.5"
          y="6.5"
          width="5"
          height="9"
          rx="0.5"
          fill="var(--stroke-color)"
          stroke="var(--stroke-color)"
          transform="rotate(-90 63.5 6.5)" />
        <rect
          x="76.25"
          y="24.75"
          width="19.5"
          height="0.5"
          fill="var(--stroke-color)"
          stroke="var(--stroke-color)"
          stroke-width="0.5"
          transform="rotate(-90 76.25 24.75)" />
        <mask
          id="path-15-outside-3_13257_34912"
          maskUnits="userSpaceOnUse"
          x="83"
          y="26"
          width="3"
          height="4"
          fill="black">
          <rect x="83" y="26" width="3" height="4" fill="white" />
          <path
            d="M85 28.5C85 28.7761 84.7761 29 84.5 29C84.2239 29 84 28.7761 84 28.5L84 27L85 27L85 28.5Z" />
        </mask>
        <path
          d="M85 28.5C85 28.7761 84.7761 29 84.5 29C84.2239 29 84 28.7761 84 28.5L84 27L85 27L85 28.5Z"
          fill="var(--light-fill-color)" />
        <path
          d="M84 27L84 26L83 26L83 27L84 27ZM85 27L86 27L86 26L85 26L85 27ZM84.5 29M84 28.5L85 28.5L85 27L84 27L83 27L83 28.5L84 28.5ZM84 27L84 28L85 28L85 27L85 26L84 26L84 27ZM85 27L84 27L84 28.5L85 28.5L86 28.5L86 27L85 27ZM84.5 29L84.5 28C84.7761 28 85 28.2239 85 28.5L84 28.5L83 28.5C83 29.3284 83.6716 30 84.5 30L84.5 29ZM84.5 29L84.5 30C85.3284 30 86 29.3284 86 28.5L85 28.5L84 28.5C84 28.2239 84.2239 28 84.5 28L84.5 29Z"
          fill="var(--stroke-color)"
          mask="url(#path-15-outside-3_13257_34912)" />
        <path
          d="M77 27L77 3L93 3C95.7614 3 98 5.23858 98 8L98 22C98 24.7614 95.7614 27 93 27L77 27Z"
          fill="var(--stroke-color)" />
        <path
          d="M77.5 3.5L91 3.5C94.0376 3.5 96.5 5.96244 96.5 9L96.5 21C96.5 24.0376 94.0376 26.5 91 26.5L77.5 26.5L77.5 3.5Z"
          fill="var(--light-fill-color)"
          stroke="var(--stroke-color)" />
        <path
          d="M87.5 4.66504L98.416 7.81348C99.058 7.9988 99.5 8.58667 99.5 9.25489L99.5 20.7451C99.5 21.4133 99.058 22.0012 98.416 22.1865L87.5 25.335L87.5 4.66504Z"
          fill="var(--light-fill-color)"
          stroke="var(--stroke-color)" />
        <rect
          x="86.25"
          y="25.75"
          width="21.5"
          height="0.5"
          fill="var(--stroke-color)"
          stroke="var(--stroke-color)"
          stroke-width="0.5"
          transform="rotate(-90 86.25 25.75)" />
        <path
          d="M77.5 3.5L84 3.5C85.3807 3.5 86.5 4.61929 86.5 6L86.5 24C86.5 25.3807 85.3807 26.5 84 26.5L77.5 26.5L77.5 3.5Z"
          fill="var(--light-fill-color)"
          stroke="var(--stroke-color)" />
        <mask
          id="path-22-outside-4_13257_34912"
          maskUnits="userSpaceOnUse"
          x="83"
          y="4.28372e-06"
          width="3"
          height="4"
          fill="black">
          <rect x="83" y="4.28372e-06" width="3" height="4" fill="white" />
          <path
            d="M85 3L84 3L84 1.5C84 1.22386 84.2239 1 84.5 1C84.7761 1 85 1.22386 85 1.5L85 3Z" />
        </mask>
        <path
          d="M85 3L84 3L84 1.5C84 1.22386 84.2239 1 84.5 1C84.7761 1 85 1.22386 85 1.5L85 3Z"
          fill="var(--light-fill-color)" />
        <path
          d="M85 3L85 4L86 4L86 3L85 3ZM84 3L83 3L83 4L84 4L84 3ZM85 3L85 2L84 2L84 3L84 4L85 4L85 3ZM84 3L85 3L85 1.5L84 1.5L83 1.5L83 3L84 3ZM84.5 1M85 1.5L84 1.5L84 3L85 3L86 3L86 1.5L85 1.5ZM84.5 1L84.5 2C84.2239 2 84 1.77615 84 1.5L85 1.5L86 1.5C86 0.671577 85.3284 4.34243e-06 84.5 4.41485e-06L84.5 1ZM84 1.5L85 1.5C85 1.77615 84.7761 2 84.5 2L84.5 1L84.5 4.41485e-06C83.6716 4.48727e-06 83 0.671577 83 1.5L84 1.5Z"
          fill="var(--stroke-color)"
          mask="url(#path-22-outside-4_13257_34912)" />
      </svg>

      <svg
        v-if="containerCount > 0"
        class="truck-bed-horizontal position-absolute"
        viewBox="0 0 100 30"
        fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <g v-if="isAssetTrailer" class="truck-trailer is-empty">
          <rect
            x="1.31134e-06"
            y="30"
            width="30"
            height="76"
            rx="2"
            :fill="isUnknownState ? 'var(--light-fill-color)' : 'white'"
            transform="rotate(-90 1.31134e-06 30)" />

          <rect
            x="3.65"
            y="26.35"
            width="22.7"
            height="68.7"
            stroke="var(--dark-fill-color)"
            stroke-width="1.3"
            transform="rotate(-90 3.65 26.35)" />
          <rect
            x="2.1582"
            y="29.6045"
            width="1.3"
            height="3.68304"
            fill="var(--dark-fill-color)"
            transform="rotate(-135 2.1582 29.6045)" />
          <rect
            width="1.3"
            height="3.68304"
            fill="var(--dark-fill-color)"
            transform="matrix(0.707107 -0.707107 -0.707107 -0.707107 3.07031 4)" />
          <rect
            x="74.6855"
            y="0.476567"
            width="1.3"
            height="3.68304"
            fill="var(--dark-fill-color)"
            transform="rotate(45 74.6855 0.476567)" />
          <rect
            width="1.3"
            height="3.68304"
            fill="var(--dark-fill-color)"
            transform="matrix(0.707107 -0.707107 -0.707107 -0.707107 74.6035 29.6045)" />
        </g>

        <g v-else-if="isAssetContainer" class="truck-container is-empty">
          <rect
            x="1.31134e-06"
            y="30"
            width="30"
            height="76"
            rx="2"
            :fill="isUnknownState ? 'var(--light-fill-color)' : 'white'"
            transform="rotate(-90 1.31134e-06 30)" />

          <rect
            x="5.55078"
            y="26"
            width="22"
            height="1.3"
            transform="rotate(-90 5.55078 26)"
            fill="var(--dark-fill-color)" />
          <rect
            x="10.8496"
            y="26"
            width="22"
            height="1.3"
            transform="rotate(-90 10.8496 26)"
            fill="var(--dark-fill-color)" />
          <rect
            x="16.1504"
            y="26"
            width="22"
            height="1.3"
            transform="rotate(-90 16.1504 26)"
            fill="var(--dark-fill-color)" />
          <rect
            x="21.4492"
            y="26"
            width="22"
            height="1.3"
            transform="rotate(-90 21.4492 26)"
            fill="var(--dark-fill-color)" />
          <rect
            x="26.75"
            y="26"
            width="22"
            height="1.3"
            transform="rotate(-90 26.75 26)"
            fill="var(--dark-fill-color)" />
          <rect
            x="32.0508"
            y="26"
            width="22"
            height="1.3"
            transform="rotate(-90 32.0508 26)"
            fill="var(--dark-fill-color)" />
          <rect
            x="37.3496"
            y="26"
            width="22"
            height="1.3"
            transform="rotate(-90 37.3496 26)"
            fill="var(--dark-fill-color)" />
          <rect
            x="42.6504"
            y="26"
            width="22"
            height="1.3"
            transform="rotate(-90 42.6504 26)"
            fill="var(--dark-fill-color)" />
          <rect
            x="47.9492"
            y="26"
            width="22"
            height="1.3"
            transform="rotate(-90 47.9492 26)"
            fill="var(--dark-fill-color)" />
          <rect
            x="53.25"
            y="26"
            width="22"
            height="1.3"
            transform="rotate(-90 53.25 26)"
            fill="var(--dark-fill-color)" />
          <rect
            x="58.5508"
            y="26"
            width="22"
            height="1.3"
            transform="rotate(-90 58.5508 26)"
            fill="var(--dark-fill-color)" />
          <rect
            x="63.8496"
            y="26"
            width="22"
            height="1.3"
            transform="rotate(-90 63.8496 26)"
            fill="var(--dark-fill-color)" />
          <rect
            x="69.1504"
            y="26"
            width="22"
            height="1.3"
            transform="rotate(-90 69.1504 26)"
            fill="var(--dark-fill-color)" />
          <rect
            v-if="isDetachableVisit"
            width="22"
            height="17"
            transform="translate(55 25.9981) rotate(-90)"
            :fill="isUnknownState ? 'var(--light-fill-color)' : 'white'" />
        </g>

        <g v-else-if="isAssetOther" class="truck-other is-empty">
          <rect
            x="1.31134e-06"
            y="30"
            width="30"
            height="76"
            rx="2"
            :fill="isUnknownState ? 'var(--light-fill-color)' : 'white'"
            transform="rotate(-90 1.31134e-06 30)" />

          <mask
            :id="`truck-other-mask-h-${svgDefUid}`"
            style="mask-type: alpha"
            maskUnits="userSpaceOnUse"
            x="1"
            y="1"
            width="74"
            height="28">
            <rect x="1" y="29" width="28" height="20" transform="rotate(-90 1 29)" fill="white" />
            <rect x="55" y="29" width="28" height="20" transform="rotate(-90 55 29)" fill="white" />
          </mask>

          <g :mask="`url(#truck-other-mask-h-${svgDefUid})`">
            <path
              d="M0.333113 29.4317L76.3153 0.939933"
              stroke="var(--dark-fill-color)"
              stroke-width="1.3" />
            <path
              d="M0.33339 0.566248L76.1553 28.998"
              stroke="var(--dark-fill-color)"
              stroke-width="1.3" />
          </g>
        </g>

        <rect
          v-else
          x="1.31134e-06"
          y="30"
          width="30"
          height="76"
          rx="2"
          fill="var(--light-fill-color)"
          transform="rotate(-90 1.31134e-06 30)" />

        <path
          v-if="isDetachableVisit"
          class="hook is-empty"
          :d="HOOK_PATH"
          fill="var(--dark-fill-color)"
          transform="translate(0 30) rotate(-90)" />

        <!-- Everything under a white pixel will be visible; Everything under a black pixel will be invisible -->
        <mask v-if="true" :id="`container-reveal-mask-h-${svgDefUid}`" mask-type="luminance">
          <rect
            :x="CONTAINER_X"
            :y="CONTAINER_Y"
            :width="CONTAINER_HEIGHT"
            :height="CONTAINER_WIDTH"
            fill="white" />
          <rect
            id="fillingRevealRectHorizontal"
            class="reveal-mask"
            :x="CONTAINER_X"
            :y="CONTAINER_Y"
            :width="revealWidth"
            :height="CONTAINER_WIDTH"
            fill="black" />
        </mask>

        <g
          v-if="isAssetTrailer"
          class="truck-trailer is-full"
          :mask="`url(#container-reveal-mask-h-${svgDefUid})`">
          <rect
            x="1.31134e-06"
            y="30"
            width="30"
            height="76"
            rx="2"
            fill="var(--dark-fill-color)"
            transform="rotate(-90 1.31134e-06 30)" />

          <rect
            x="3.65"
            y="26.35"
            width="22.7"
            height="68.7"
            stroke="white"
            stroke-width="1.3"
            transform="rotate(-90 3.65 26.35)" />
          <rect
            x="2.1582"
            y="29.6045"
            width="1.3"
            height="3.68304"
            fill="white"
            transform="rotate(-135 2.1582 29.6045)" />
          <rect
            width="1.3"
            height="3.68304"
            fill="white"
            transform="matrix(0.707107 -0.707107 -0.707107 -0.707107 3.07031 4)" />
          <rect
            x="74.6855"
            y="0.476567"
            width="1.3"
            height="3.68304"
            fill="white"
            transform="rotate(45 74.6855 0.476567)" />
          <rect
            width="1.3"
            height="3.68304"
            fill="white"
            transform="matrix(0.707107 -0.707107 -0.707107 -0.707107 74.6035 29.6045)" />
        </g>

        <g
          v-else-if="isAssetContainer"
          class="truck-container is-full"
          :mask="`url(#container-reveal-mask-h-${svgDefUid})`">
          <rect
            x="1.31134e-06"
            y="30"
            width="30"
            height="76"
            rx="2"
            fill="var(--dark-fill-color)"
            transform="rotate(-90 1.31134e-06 30)" />

          <rect
            x="5.55078"
            y="26"
            width="22"
            height="1.3"
            transform="rotate(-90 5.55078 26)"
            fill="white" />
          <rect
            x="10.8496"
            y="26"
            width="22"
            height="1.3"
            transform="rotate(-90 10.8496 26)"
            fill="white" />
          <rect
            x="16.1504"
            y="26"
            width="22"
            height="1.3"
            transform="rotate(-90 16.1504 26)"
            fill="white" />
          <rect
            x="21.4492"
            y="26"
            width="22"
            height="1.3"
            transform="rotate(-90 21.4492 26)"
            fill="white" />
          <rect
            x="26.75"
            y="26"
            width="22"
            height="1.3"
            transform="rotate(-90 26.75 26)"
            fill="white" />
          <rect
            x="32.0508"
            y="26"
            width="22"
            height="1.3"
            transform="rotate(-90 32.0508 26)"
            fill="white" />
          <rect
            x="37.3496"
            y="26"
            width="22"
            height="1.3"
            transform="rotate(-90 37.3496 26)"
            fill="white" />
          <rect
            x="42.6504"
            y="26"
            width="22"
            height="1.3"
            transform="rotate(-90 42.6504 26)"
            fill="white" />
          <rect
            x="47.9492"
            y="26"
            width="22"
            height="1.3"
            transform="rotate(-90 47.9492 26)"
            fill="white" />
          <rect
            x="53.25"
            y="26"
            width="22"
            height="1.3"
            transform="rotate(-90 53.25 26)"
            fill="white" />
          <rect
            x="58.5508"
            y="26"
            width="22"
            height="1.3"
            transform="rotate(-90 58.5508 26)"
            fill="white" />
          <rect
            x="63.8496"
            y="26"
            width="22"
            height="1.3"
            transform="rotate(-90 63.8496 26)"
            fill="white" />
          <rect
            x="69.1504"
            y="26"
            width="22"
            height="1.3"
            transform="rotate(-90 69.1504 26)"
            fill="white" />
          <rect
            v-if="isDetachableVisit"
            width="22"
            height="17"
            transform="translate(55 25.9981) rotate(-90)"
            fill="var(--dark-fill-color)" />
        </g>

        <g
          v-else-if="isAssetOther"
          class="truck-other is-full"
          :mask="`url(#container-reveal-mask-h-${svgDefUid})`">
          <rect
            x="1.31134e-06"
            y="30"
            width="30"
            height="76"
            rx="2"
            fill="var(--dark-fill-color)"
            transform="rotate(-90 1.31134e-06 30)" />

          <mask
            :id="`truck-other-mask-h-${svgDefUid}`"
            style="mask-type: alpha"
            maskUnits="userSpaceOnUse"
            x="1"
            y="1"
            width="74"
            height="28">
            <rect x="1" y="29" width="28" height="20" transform="rotate(-90 1 29)" fill="white" />
            <rect x="55" y="29" width="28" height="20" transform="rotate(-90 55 29)" fill="white" />
          </mask>

          <g :mask="`url(#truck-other-mask-h-${svgDefUid})`">
            <path d="M0.333113 29.4317L76.3153 0.939933" stroke="white" stroke-width="1.3" />
            <path d="M0.33339 0.566248L76.1553 28.998" stroke="white" stroke-width="1.3" />
          </g>
        </g>

        <!-- we need this group in order to apply the mask in the translated path -->
        <g v-if="isDetachableVisit" :mask="`url(#container-reveal-mask-h-${svgDefUid})`">
          <path
            class="hook is-full"
            :d="HOOK_PATH"
            fill="white"
            transform="translate(0 30) rotate(-90)" />
        </g>

        <rect
          x="0.500001"
          y="29.5"
          width="29"
          height="75"
          rx="1.5"
          stroke="var(--stroke-color)"
          transform="rotate(-90 0.500001 29.5)" />
      </svg>
    </template>

    <template v-if="orientation === 'vertical'">
      <svg
        class="truck-cab-vertical"
        viewBox="0 0 30 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <rect width="30" height="100" fill="white" />
        <path
          d="M-1535 -324C-1535 -354.928 -1509.93 -380 -1479 -380H8489C8519.93 -380 8545 -354.928 8545 -324V1546C8545 1576.93 8519.93 1602 8489 1602H-1479C-1509.93 1602 -1535 1576.93 -1535 1546V-324Z"
          fill="#C9D0D4" />
        <path
          d="M-1479 -380V-372H8489V-380V-388H-1479V-380ZM8545 -324H8537V1546H8545H8553V-324H8545ZM8489 1602V1594H-1479V1602V1610H8489V1602ZM-1535 1546H-1527V-324H-1535H-1543V1546H-1535ZM-1479 1602V1594C-1505.51 1594 -1527 1572.51 -1527 1546H-1535H-1543C-1543 1581.35 -1514.35 1610 -1479 1610V1602ZM8545 1546H8537C8537 1572.51 8515.51 1594 8489 1594V1602V1610C8524.35 1610 8553 1581.35 8553 1546H8545ZM8489 -380V-372C8515.51 -372 8537 -350.51 8537 -324H8545H8553C8553 -359.346 8524.35 -388 8489 -388V-380ZM-1479 -380V-388C-1514.35 -388 -1543 -359.346 -1543 -324H-1535H-1527C-1527 -350.51 -1505.51 -372 -1479 -372V-380Z"
          fill="#3D4A55"
          fill-opacity="0.5" />
        <rect x="-50" y="-50" width="433" height="200" rx="16" fill="white" />
        <path
          d="M2 2C2 0.895431 2.89543 -3.91405e-08 4 -8.74228e-08L26 -1.04907e-06C27.1046 -1.09736e-06 28 0.895429 28 2L2 2Z"
          fill="var(--stroke-color)" />
        <rect
          x="1.5"
          y="5.5"
          width="5"
          height="9"
          rx="0.5"
          fill="var(--stroke-color)"
          stroke="var(--stroke-color)" />
        <rect
          x="1.5"
          y="16.5"
          width="5"
          height="9"
          rx="0.5"
          fill="var(--stroke-color)"
          stroke="var(--stroke-color)" />
        <rect
          x="1.5"
          y="52.5"
          width="5"
          height="9"
          rx="0.5"
          fill="var(--stroke-color)"
          stroke="var(--stroke-color)" />
        <rect
          x="1.5"
          y="63.5"
          width="5"
          height="9"
          rx="0.5"
          fill="var(--stroke-color)"
          stroke="var(--stroke-color)" />
        <g clip-path="url(#clip0_507_121)">
          <mask id="path-9-inside-1_507_121" fill="white">
            <path d="M7 2H11V76H7V2Z" />
          </mask>
          <path d="M7 2H11V76H7V2Z" fill="var(--light-fill-color)" />
          <path
            d="M11 2H10V76H11H12V2H11ZM7 76H8V2H7H6V76H7Z"
            fill="var(--stroke-color)"
            mask="url(#path-9-inside-1_507_121)" />
          <mask id="path-11-inside-2_507_121" fill="white">
            <path d="M19 2H23V76H19V2Z" />
          </mask>
          <path d="M19 2H23V76H19V2Z" fill="var(--light-fill-color)" />
          <path
            d="M23 2H22V76H23H24V2H23ZM19 76H20V2H19H18V76H19Z"
            fill="var(--stroke-color)"
            mask="url(#path-11-inside-2_507_121)" />
        </g>
        <rect
          x="23.5"
          y="5.5"
          width="5"
          height="9"
          rx="0.5"
          fill="var(--stroke-color)"
          stroke="var(--stroke-color)" />
        <rect
          x="23.5"
          y="16.5"
          width="5"
          height="9"
          rx="0.5"
          fill="var(--stroke-color)"
          stroke="var(--stroke-color)" />
        <rect
          x="23.5"
          y="52.5"
          width="5"
          height="9"
          rx="0.5"
          fill="var(--stroke-color)"
          stroke="var(--stroke-color)" />
        <rect
          x="23.5"
          y="63.5"
          width="5"
          height="9"
          rx="0.5"
          fill="var(--stroke-color)"
          stroke="var(--stroke-color)" />
        <rect
          x="5.25"
          y="76.25"
          width="19.5"
          height="0.5"
          fill="var(--stroke-color)"
          stroke="var(--stroke-color)"
          stroke-width="0.5" />
        <mask
          id="path-18-outside-3_507_121"
          maskUnits="userSpaceOnUse"
          x="-8.74228e-08"
          y="83"
          width="4"
          height="3"
          fill="black">
          <rect x="-8.74228e-08" y="83" width="4" height="3" fill="white" />
          <path
            d="M1.5 85C1.22386 85 1 84.7761 1 84.5C1 84.2239 1.22386 84 1.5 84L3 84L3 85L1.5 85Z" />
        </mask>
        <path
          d="M1.5 85C1.22386 85 1 84.7761 1 84.5C1 84.2239 1.22386 84 1.5 84L3 84L3 85L1.5 85Z"
          fill="var(--light-fill-color)" />
        <path
          d="M3 84L4 84L4 83L3 83L3 84ZM3 85L3 86L4 86L4 85L3 85ZM1.5 84L1.5 85L3 85L3 84L3 83L1.5 83L1.5 84ZM3 84L2 84L2 85L3 85L4 85L4 84L3 84ZM3 85L3 84L1.5 84L1.5 85L1.5 86L3 86L3 85ZM1 84.5L2 84.5C2 84.7761 1.77614 85 1.5 85L1.5 84L1.5 83C0.671573 83 -5.80674e-08 83.6716 -2.18557e-08 84.5L1 84.5ZM1 84.5L-2.18557e-08 84.5C1.4356e-08 85.3284 0.671573 86 1.5 86L1.5 85L1.5 84C1.77614 84 2 84.2239 2 84.5L1 84.5Z"
          fill="var(--stroke-color)"
          mask="url(#path-18-outside-3_507_121)" />
        <path
          d="M3 77H27V93C27 95.7614 24.7614 98 22 98H8C5.23858 98 3 95.7614 3 93V77Z"
          fill="var(--stroke-color)" />
        <path
          d="M26.5 77.5V91C26.5 94.0376 24.0376 96.5 21 96.5H9C5.96243 96.5 3.5 94.0376 3.5 91V77.5H26.5Z"
          fill="var(--light-fill-color)"
          stroke="var(--stroke-color)" />
        <path
          d="M25.335 87.5L22.1865 98.416C22.0012 99.058 21.4133 99.5 20.7451 99.5H9.25488C8.58667 99.5 7.99879 99.058 7.81348 98.416L4.66504 87.5H25.335Z"
          fill="var(--light-fill-color)"
          stroke="var(--stroke-color)" />
        <rect
          x="4.25"
          y="86.25"
          width="21.5"
          height="0.5"
          fill="var(--stroke-color)"
          stroke="var(--stroke-color)"
          stroke-width="0.5" />
        <path
          d="M26.5 77.5V84C26.5 85.3807 25.3807 86.5 24 86.5H6C4.61929 86.5 3.5 85.3807 3.5 84V77.5H26.5Z"
          fill="var(--light-fill-color)"
          stroke="var(--stroke-color)" />
        <mask
          id="path-25-outside-4_507_121"
          maskUnits="userSpaceOnUse"
          x="26"
          y="83"
          width="4"
          height="3"
          fill="black">
          <rect x="26" y="83" width="4" height="3" fill="white" />
          <path
            d="M27 85L27 84L28.5 84C28.7761 84 29 84.2239 29 84.5C29 84.7761 28.7761 85 28.5 85L27 85Z" />
        </mask>
        <path
          d="M27 85L27 84L28.5 84C28.7761 84 29 84.2239 29 84.5C29 84.7761 28.7761 85 28.5 85L27 85Z"
          fill="var(--light-fill-color)" />
        <path
          d="M27 85L26 85L26 86L27 86L27 85ZM27 84L27 83L26 83L26 84L27 84ZM27 85L28 85L28 84L27 84L26 84L26 85L27 85ZM27 84L27 85L28.5 85L28.5 84L28.5 83L27 83L27 84ZM28.5 85L28.5 84L27 84L27 85L27 86L28.5 86L28.5 85ZM29 84.5L28 84.5C28 84.2239 28.2239 84 28.5 84L28.5 85L28.5 86C29.3284 86 30 85.3284 30 84.5L29 84.5ZM28.5 84L28.5 85C28.2239 85 28 84.7761 28 84.5L29 84.5L30 84.5C30 83.6716 29.3284 83 28.5 83L28.5 84Z"
          fill="var(--stroke-color)"
          mask="url(#path-25-outside-4_507_121)" />
      </svg>

      <svg
        v-if="containerCount > 0"
        class="truck-bed-vertical position-absolute"
        viewBox="0 0 30 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <g v-if="isAssetTrailer" class="truck-trailer is-empty">
          <rect
            width="30"
            height="76"
            rx="2"
            :fill="isUnknownState ? 'var(--light-fill-color)' : 'white'" />

          <rect
            x="3.65"
            y="3.65"
            width="22.7"
            height="68.7"
            stroke="var(--dark-fill-color)"
            stroke-width="1.3" />
          <rect
            x="0.396484"
            y="2.15723"
            width="1.3"
            height="3.68304"
            fill="var(--dark-fill-color)"
            transform="rotate(-45 0.396484 2.15723)" />
          <rect
            width="1.3"
            height="3.68304"
            fill="var(--dark-fill-color)"
            transform="matrix(0.707107 0.707107 0.707107 -0.707107 26 3.07129)" />
          <rect
            x="29.5234"
            y="74.6855"
            width="1.3"
            height="3.68304"
            fill="var(--dark-fill-color)"
            transform="rotate(135 29.5234 74.6855)" />
          <rect
            width="1.3"
            height="3.68304"
            fill="var(--dark-fill-color)"
            transform="matrix(0.707107 0.707107 0.707107 -0.707107 0.396484 74.6045)" />
        </g>

        <g v-else-if="isAssetContainer" class="truck-container is-empty">
          <rect
            width="30"
            height="76"
            rx="2"
            :fill="isUnknownState ? 'var(--light-fill-color)' : 'white'" />

          <rect x="4" y="5.5498" width="22" height="1.3" fill="var(--dark-fill-color)" />
          <rect x="4" y="10.8496" width="22" height="1.3" fill="var(--dark-fill-color)" />
          <rect x="4" y="16.1504" width="22" height="1.3" fill="var(--dark-fill-color)" />
          <rect x="4" y="21.4502" width="22" height="1.3" fill="var(--dark-fill-color)" />
          <rect x="4" y="26.75" width="22" height="1.3" fill="var(--dark-fill-color)" />
          <rect x="4" y="32.0498" width="22" height="1.3" fill="var(--dark-fill-color)" />
          <rect x="4" y="37.3496" width="22" height="1.3" fill="var(--dark-fill-color)" />
          <rect x="4" y="42.6504" width="22" height="1.3" fill="var(--dark-fill-color)" />
          <rect x="4" y="47.9502" width="22" height="1.3" fill="var(--dark-fill-color)" />
          <rect x="4" y="53.25" width="22" height="1.3" fill="var(--dark-fill-color)" />
          <rect x="4" y="58.5498" width="22" height="1.3" fill="var(--dark-fill-color)" />
          <rect x="4" y="63.8496" width="22" height="1.3" fill="var(--dark-fill-color)" />
          <rect x="4" y="69.1504" width="22" height="1.3" fill="var(--dark-fill-color)" />
          <rect
            v-if="isDetachableVisit"
            width="22"
            height="17"
            transform="translate(4 55)"
            :fill="isUnknownState ? 'var(--light-fill-color)' : 'white'" />
        </g>

        <g v-else-if="isAssetOther" class="truck-other is-empty">
          <rect
            width="30"
            height="76"
            rx="2"
            :fill="isUnknownState ? 'var(--light-fill-color)' : 'white'" />

          <mask
            :id="`truck-other-mask-v-${svgDefUid}`"
            style="mask-type: alpha"
            maskUnits="userSpaceOnUse"
            x="1"
            y="1"
            width="28"
            height="74">
            <rect x="1" y="1" width="28" height="20" fill="var(--dark-fill-color)" />
            <rect x="1" y="55" width="28" height="20" fill="var(--dark-fill-color)" />
          </mask>
          <g :mask="`url(#truck-other-mask-v-${svgDefUid})`">
            <path
              d="M0.569236 0.332134L29.061 76.3143"
              stroke="var(--dark-fill-color)"
              stroke-width="1.3" />
            <path
              d="M29.4347 0.332413L1.00302 76.1543"
              stroke="var(--dark-fill-color)"
              stroke-width="1.3" />
          </g>
        </g>

        <rect v-else width="30" height="76" rx="2" fill="var(--light-fill-color)" />

        <path
          v-if="isDetachableVisit"
          class="hook is-empty"
          :d="HOOK_PATH"
          fill="var(--dark-fill-color)" />

        <!-- Everything under a white pixel will be visible; Everything under a black pixel will be invisible -->
        <mask v-if="true" :id="`container-reveal-mask-v-${svgDefUid}`" mask-type="luminance">
          <rect
            :x="CONTAINER_X"
            :y="CONTAINER_Y"
            :width="CONTAINER_WIDTH"
            :height="CONTAINER_HEIGHT"
            fill="white" />
          <rect
            id="fillingRevealRectHorizontal"
            class="reveal-mask"
            :x="CONTAINER_X"
            :y="CONTAINER_Y"
            :width="CONTAINER_WIDTH"
            :height="revealWidth"
            fill="black" />
        </mask>

        <g
          v-if="isAssetTrailer"
          class="truck-trailer is-full"
          :mask="`url(#container-reveal-mask-v-${svgDefUid})`">
          <rect width="30" height="76" rx="2" fill="var(--dark-fill-color)" />
          <rect x="3.65" y="3.65" width="22.7" height="68.7" stroke="white" stroke-width="1.3" />
          <rect
            x="0.396484"
            y="2.15723"
            width="1.3"
            height="3.68304"
            fill="white"
            transform="rotate(-45 0.396484 2.15723)" />
          <rect
            width="1.3"
            height="3.68304"
            fill="white"
            transform="matrix(0.707107 0.707107 0.707107 -0.707107 26 3.07129)" />
          <rect
            x="29.5234"
            y="74.6855"
            width="1.3"
            height="3.68304"
            fill="white"
            transform="rotate(135 29.5234 74.6855)" />
          <rect
            width="1.3"
            height="3.68304"
            fill="white"
            transform="matrix(0.707107 0.707107 0.707107 -0.707107 0.396484 74.6045)" />
        </g>

        <g
          v-else-if="isAssetContainer"
          class="truck-container is-full"
          :mask="`url(#container-reveal-mask-v-${svgDefUid})`">
          <rect width="30" height="76" rx="2" fill="var(--dark-fill-color)" />
          <rect x="4" y="5.5498" width="22" height="1.3" fill="white" />
          <rect x="4" y="10.8496" width="22" height="1.3" fill="white" />
          <rect x="4" y="16.1504" width="22" height="1.3" fill="white" />
          <rect x="4" y="21.4502" width="22" height="1.3" fill="white" />
          <rect x="4" y="26.75" width="22" height="1.3" fill="white" />
          <rect x="4" y="32.0498" width="22" height="1.3" fill="white" />
          <rect x="4" y="37.3496" width="22" height="1.3" fill="white" />
          <rect x="4" y="42.6504" width="22" height="1.3" fill="white" />
          <rect x="4" y="47.9502" width="22" height="1.3" fill="white" />
          <rect x="4" y="53.25" width="22" height="1.3" fill="white" />
          <rect x="4" y="58.5498" width="22" height="1.3" fill="white" />
          <rect x="4" y="63.8496" width="22" height="1.3" fill="white" />
          <rect x="4" y="69.1504" width="22" height="1.3" fill="white" />
          <rect width="22" height="17" transform="translate(4 55)" fill="var(--dark-fill-color)" />
        </g>

        <g
          v-else-if="isAssetOther"
          class="truck-other is-full"
          :mask="`url(#container-reveal-mask-v-${svgDefUid})`">
          <rect width="30" height="76" rx="2" fill="var(--dark-fill-color)" />

          <mask
            :id="`truck-other-mask-v-${svgDefUid}`"
            style="mask-type: alpha"
            maskUnits="userSpaceOnUse"
            x="1"
            y="1"
            width="28"
            height="74">
            <rect x="1" y="1" width="28" height="20" fill="white" />
            <rect x="1" y="55" width="28" height="20" fill="white" />
          </mask>

          <g :mask="`url(#truck-other-mask-v-${svgDefUid})`">
            <path d="M0.569236 0.332134L29.061 76.3143" stroke="white" stroke-width="1.3" />
            <path d="M29.4347 0.332413L1.00302 76.1543" stroke="white" stroke-width="1.3" />
          </g>
        </g>

        <path
          v-if="isDetachableVisit"
          class="hook is-full"
          :d="HOOK_PATH"
          fill="white"
          :mask="`url(#container-reveal-mask-v-${svgDefUid})`" />

        <rect x="0.5" y="0.5" width="29" height="75" rx="1.5" stroke="var(--stroke-color)" />
      </svg>
    </template>

    <span
      v-if="containerCount > 1 && showContainerCount"
      class="position-absolute d-flex justify-center align-center top-0 left-0"
      :style="{
        blockSize: `${
          orientation === 'horizontal' ? `${CONTAINER_WIDTH}px` : `${CONTAINER_HEIGHT}px`
        }`,
        inlineSize: `${
          orientation === 'horizontal' ? `${CONTAINER_HEIGHT}px` : `${CONTAINER_WIDTH}px`
        }`,
        top: 0,
        left: 0
      }">
      <span
        class="d-flex justify-center align-center text--color-text-inverted typography-caption-bold"
        style="
          inline-size: 16px;
          block-size: 16px;
          box-sizing: border-box;
          background-color: var(--stroke-color);
          border-radius: 8px;
          line-height: 16px;
        ">
        {{ containerCount }}
      </span>
    </span>
  </span>
</template>

<style scoped>
.asset-visit-state-icon-v2 {
  position: relative;
  display: flex;
  flex-direction: column;

  opacity: 1;
}

.-horizontal {
  width: 100px;
  height: 30px;
}

.-vertical {
  width: 30px;
  height: 100px;
}

.is-loading {
  opacity: 0.15;
}

.-past-appointment {
  --light-fill-color: #faebee;
  --dark-fill-color: #f20c59;
  --stroke-color: #d40047;
}

.-active-appointment {
  --light-fill-color: #e3efff;
  --dark-fill-color: #0c6cf2;
  --stroke-color: #005ad8;
}

.-no-appointment {
  --light-fill-color: #f2f2f2;
  --dark-fill-color: #737373;
  --stroke-color: #656565;
}

svg {
  display: block;
  width: 100%;
  height: 100%;
}

@media (prefers-reduced-motion: no-preference) {
  .reveal-mask {
    will-change: height, width;
    transition: height 2.5s ease-in-out, width 2.5s ease-in-out;
  }

  .asset-visit-state-icon-v2 {
    will-change: opacity;
    transition: opacity 0.35s ease-in-out;
  }
}
</style>
